/**
 * AdventureTitleImageFromId Component
 * 
 * Fetches the adventure header to get the actual title image ID,
 * then displays the title image. Used for <adventure> tags in rich text.
 */

import React, { useState, useEffect } from 'react';
import { AdventureTitleImage } from './AdventureTitleImage';
import apiService from '../../services/api';

export interface AdventureTitleImageFromIdProps {
  /**
   * Adventure ID to fetch title image for
   */
  adventureId: string;
  
  /**
   * Size variant to display
   */
  variant?: 'thumbnail' | 'card' | 'large';
  
  /**
   * Alt text for the image
   */
  alt?: string;
  
  /**
   * Additional CSS classes
   */
  className?: string;
}

/**
 * Component that fetches adventure header and displays the title image
 */
export const AdventureTitleImageFromId: React.FC<AdventureTitleImageFromIdProps> = ({
  adventureId,
  variant = 'card',
  alt,
  className = ''
}) => {
  const [titleImageId, setTitleImageId] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const fetchTitleImageId = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        const header = await apiService.getAdventureHeader(adventureId);
        if (isMounted) {
          setTitleImageId(header.titleImageId);
          setIsLoading(false);
        }
      } catch (err) {
        console.warn(`Failed to fetch adventure header for ${adventureId}:`, err);
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Failed to load adventure');
          setIsLoading(false);
        }
      }
    };

    fetchTitleImageId();

    return () => {
      isMounted = false;
    };
  }, [adventureId]);

  if (isLoading) {
    return (
      <div className={`rich-text-adventure-loading ${className}`} style={{
        backgroundColor: 'rgba(0, 0, 0, 0.3)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        borderRadius: '0.5rem',
        padding: '1rem',
        minHeight: '64px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <span className="text-gray-400 text-sm">Loading...</span>
      </div>
    );
  }

  if (error || !titleImageId) {
    return (
      <div className={`rich-text-adventure-error ${className}`} style={{
        backgroundColor: 'rgba(0, 0, 0, 0.3)',
        border: '1px solid rgba(255, 0, 0, 0.3)',
        borderRadius: '0.5rem',
        padding: '1rem',
        minHeight: '64px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <span className="text-red-400 text-sm">🖼️ Image not available</span>
      </div>
    );
  }

  return (
    <AdventureTitleImage
      titleImageId={titleImageId}
      variant={variant}
      alt={alt || `Adventure: ${adventureId}`}
      className={className}
      responsive={true}
    />
  );
};

export default AdventureTitleImageFromId;

